import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { gapAssessmentApi, GapAssessment, GapAssessmentItem } from '../api/gapAssessment.api';

const riskColors: Record<string, string> = {
  met: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  not_met: 'bg-red-100 text-red-800',
};

const ScoreBar: React.FC<{ score?: number }> = ({ score }) => {
  if (score === undefined || score === null) return <span className="text-xs text-gray-400">Not scored yet</span>;
  const pct = Math.round(score);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-semibold text-gray-700 w-12">{pct}%</span>
    </div>
  );
};

const GapAssessmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<(GapAssessment & { items: GapAssessmentItem[] }) | null>(null);
  const [remediationPlan, setRemediationPlan] = useState<GapAssessmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'items' | 'plan'>('items');
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<number, Partial<GapAssessmentItem>>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    if (!id) return;
    setIsLoading(true);
    setError('');
    try {
      const [data, plan] = await Promise.all([
        gapAssessmentApi.get(Number(id)),
        gapAssessmentApi.getRemediationPlan(Number(id)).catch(() => []),
      ]);
      setAssessment(data);
      setRemediationPlan(plan);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleSaveItem = async (itemId: number) => {
    if (!id || !editData[itemId]) return;
    setSaving(itemId);
    try {
      await gapAssessmentApi.updateItem(Number(id), itemId, editData[itemId]);
      setExpandedItem(null);
      setEditData(prev => { const n = { ...prev }; delete n[itemId]; return n; });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    setCompleting(true);
    try {
      await gapAssessmentApi.complete(Number(id));
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCompleting(false);
    }
  };

  const toggleItem = (itemId: number, item: GapAssessmentItem) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
    } else {
      setExpandedItem(itemId);
      const itemData: Partial<GapAssessmentItem> = { current_state: item.current_state || '', risk_level: item.risk_level, remediation_notes: item.remediation_notes || '' };
      if (item.estimated_effort !== undefined) itemData.estimated_effort = item.estimated_effort;
      setEditData(prev => ({ ...prev, [itemId]: itemData }));
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  }

  if (!assessment) {
    return <div className="p-6 text-gray-500">Assessment not found.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/compliance/gap-assessments')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={18} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{assessment.title}</h1>
          <p className="text-sm text-gray-500">Framework #{assessment.framework_id}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${assessment.status === 'completed' ? 'bg-green-100 text-green-800' : assessment.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
            {assessment.status.replace('_', ' ')}
          </span>
          {assessment.status === 'in_progress' && (
            <button onClick={handleComplete} disabled={completing}
              className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
              {completing ? 'Completing...' : 'Complete Assessment'}
            </button>
          )}
          <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={16} className="text-gray-600" /></button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      <div className="bg-white border rounded-xl p-4">
        <p className="text-sm text-gray-500 mb-2">Overall Compliance Score</p>
        <ScoreBar {...(assessment.overall_score !== undefined ? { score: assessment.overall_score } : {})} />
      </div>

      <div className="border-b flex gap-1">
        {[{ key: 'items', label: `Assessment Items (${assessment.items?.length || 0})` }, { key: 'plan', label: 'Remediation Plan' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'items' && (
        <div className="space-y-2">
          {(assessment.items || []).length === 0 ? (
            <div className="py-12 text-gray-500">No items loaded. The assessment may still be populating.</div>
          ) : (assessment.items || []).map(item => (
            <div key={item.id} className="bg-white border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleItem(item.id, item)}>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    {item.requirement_ref || `Page #${item.compliance_page_id}`}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskColors[item.risk_level]}`}>
                    {item.risk_level.replace('_', ' ')}
                  </span>
                </div>
                <span className="text-xs text-gray-400">{expandedItem === item.id ? '▲' : '▼'}</span>
              </div>

              {expandedItem === item.id && editData[item.id] && (() => {
                const ed = editData[item.id]!;
                return (
                <div className="border-t p-4 space-y-3 bg-gray-50">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Current State</label>
                    <textarea value={ed.current_state || ''} rows={2}
                      onChange={e => setEditData(p => ({ ...p, [item.id]: { ...p[item.id], current_state: e.target.value } }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Describe current state..." />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Risk Level</label>
                      <select value={ed.risk_level || 'not_met'}
                        onChange={e => setEditData(p => ({ ...p, [item.id]: { ...p[item.id], risk_level: e.target.value as any } }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm">
                        <option value="met">Met ✓</option>
                        <option value="partial">Partial ⚠</option>
                        <option value="not_met">Not Met ✗</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Est. Effort (days)</label>
                      <input type="number" value={ed.estimated_effort ?? ''}
                        onChange={e => setEditData(p => ({ ...p, [item.id]: { ...p[item.id], estimated_effort: Number(e.target.value) } }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Days" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Remediation Notes</label>
                    <textarea value={ed.remediation_notes || ''} rows={2}
                      onChange={e => setEditData(p => ({ ...p, [item.id]: { ...p[item.id], remediation_notes: e.target.value } }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Steps to remediate..." />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setExpandedItem(null)} className="px-3 py-1.5 text-xs border rounded-lg">Cancel</button>
                    <button onClick={() => handleSaveItem(item.id)} disabled={saving === item.id}
                      className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {saving === item.id ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {tab === 'plan' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          {remediationPlan.length === 0 ? (
            <div className="py-12 text-gray-500">No remediation items. Score all items first.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['#', 'Requirement', 'Status', 'Effort (days)', 'Notes'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y">
                {remediationPlan.map((item, i) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-left text-gray-500 text-xs">{i + 1}</td>
                    <td className="px-4 py-3 text-left font-mono text-xs text-blue-700">{item.requirement_ref || `Page #${item.compliance_page_id}`}</td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskColors[item.risk_level]}`}>
                        {item.risk_level.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-gray-600">{item.estimated_effort ?? '-'}</td>
                    <td className="px-4 py-3 text-left text-gray-600 text-xs">{item.remediation_notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default GapAssessmentDetail;
